import {refineRange} from './acousticBoundaries.js';
import {speechHandleRange} from './speechHandles.js';
import {selectedRanges} from './selection.js';
import {interviewDisplayName} from './interviewNames.js';

// Store immutable source metadata with selects, never signed URLs or nested projects.
export function sourceSnapshot(row) {
  const {filename,duration,width,height,fpsNum,fpsDen,audioChannels,audioSampleRate,timecode,frameStatus,alignment,words}=row.data;
  return {id:row.id,title:interviewDisplayName(row.data),filename,duration,width,height,fpsNum,fpsDen,audioChannels,audioSampleRate,timecode,frameStatus,alignment,words};
}
export function addPassages(paper, source, selected, id=()=>crypto.randomUUID(), audio=null) {
  const ranges=selectedRanges(source.words,selected);
  if(!ranges.length)return paper;
  const blocks=ranges.map(range=>({
    id:id(),sourceId:source.id,
    ...(audio ? refineRange(source.words,range,audio,source.duration)
      : speechHandleRange(source.words,range,source.duration)),
    originalRange:{...range},
    automaticHandles:!audio,
    // Padding is audio handle only; do not add adjacent unselected transcript words.
    text:source.words.filter(w=>w.start<range.end && w.end>range.start).map(w=>w.text).join(' ')
  }));
  return {...paper,sources:{...paper.sources,[source.id]:source},blocks:[...paper.blocks,...blocks]};
}
// Older saved passages can opt in to safe audio handles. Exact transcript
// matches only: authored trims, refined clips and uncertain text are skipped.
export function protectLegacyWordEdges(paper) {
 if(!Array.isArray(paper?.blocks)||!paper.blocks.length)return paper;
 let changed=false;
 const blocks=paper.blocks.map(block=>{
  if(block.originalRange||paper.refine?.lockedHandles?.[block.id])return block;
  const source=paper.sources?.[block.sourceId],words=source?.words;
  if(!Array.isArray(words)||!words.length)return block;
  const first=words.findIndex(w=>w.start===block.start&&w.end>w.start);
  const last=words.findLastIndex(w=>w.end===block.end&&w.end>w.start);
  if(first<0||last<first)return block;
  const text=words.slice(first,last+1).map(w=>w.text).join(' ').trim();
  if(text!==(block.text||'').trim())return block;
  const safe=speechHandleRange(words,{start:block.start,end:block.end},source.duration);
  if(safe.start===block.start&&safe.end===block.end)return block;
  changed=true;
  return {...block,...safe,originalRange:{start:block.start,end:block.end},automaticHandles:true};
 });
 return changed?{...paper,blocks}:paper;
}

// Insert freshly collected passages without disturbing existing slip IDs or
// source references. Headings continue to mark the same pre-existing passage.
export function insertNewPassages(paper,appended,beforeIndex=null) {
  const added=appended.blocks.slice(paper.blocks.length);
  if(!added.length || beforeIndex===null || beforeIndex===undefined)return appended;
  const at=Math.max(0,Math.min(paper.blocks.length,Math.trunc(beforeIndex)));
  const blocks=[...paper.blocks.slice(0,at),...added,...paper.blocks.slice(at)];
  const sectionHeadings=(appended.sectionHeadings||paper.sectionHeadings||[]).map(heading=>({
    ...heading,beforeIndex:heading.beforeIndex>=at?heading.beforeIndex+added.length:heading.beforeIndex
  }));
  return {...appended,blocks,sectionHeadings};
}
export function movePassage(paper,from,to) {
  if(from<0 || to<0 || from>=paper.blocks.length || to>=paper.blocks.length)return paper;
  const blocks=[...paper.blocks];blocks.splice(to,0,blocks.splice(from,1)[0]);return {...paper,blocks};
}
export function trimPassage(paper,id,edge,value) {
  const blocks=paper.blocks.map(block=>{
    if(block.id!==id)return block;
    const source=paper.sources[block.sourceId];
    const next={...block,[edge]:Number(value)};
    if(!['start','end'].includes(edge) || !Number.isFinite(next[edge]) || next.start<0 || next.end>source.duration || next.end<=next.start)throw new Error('Choose an in point before the out point, within the source video.');
    return {...next,text:source.words.filter(w=>w.start<next.end && w.end>next.start).map(w=>w.text).join(' ')};
  });
  return {...paper,blocks};
}
export function validatePaperExport(paper) {
  if(!paper.blocks.length)throw new Error('Add a passage before exporting.');
  const sources=paper.blocks.map(b=>paper.sources[b.sourceId]);
  if(sources.some(s=>!s || !['confirmed-cfr','detected-cfr','demo-cfr'].includes(s.frameStatus)))throw new Error('Verify Source settings for each interview, then refresh its metadata in Paper edit before XML export.');
  for(const block of paper.blocks){const s=paper.sources[block.sourceId];if(!Number.isFinite(block.start)||!Number.isFinite(block.end)||block.start<0||block.end<=block.start||block.end>s.duration)throw new Error('A passage has invalid source timing.');}
}
